import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const LocationsTab = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ login: '', haslo: '', typ: 'kasjer' });

  // Rozwijane sekcje
  const [expandedSections, setExpandedSections] = useState({
    addLocation: false,
    locations: true,
    employees: false,
    users: false
  });

  const [newLocation, setNewLocation] = useState({
    kod_lokalizacji: '', nazwa: '', typ: 'sklep', adres: '',
    miasto: '', kod_pocztowy: '', telefon: '', email: '',
    manager_login: '', aktywny: true, godziny_otwarcia: ''
  });

  const [employeeForm, setEmployeeForm] = useState({
    user_login: '', rola: 'pracownik', uprawnienia: ''
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Style
  const headerStyle = (isExpanded) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '6px 10px', backgroundColor: isExpanded ? 'var(--card-bg)' : 'var(--bg)',
    color: isExpanded ? 'var(--info)' : 'var(--text-secondary)', border: isExpanded ? '1px solid var(--info)' : '1px solid var(--border)',
    borderRadius: isExpanded ? '4px 4px 0 0' : '4px', cursor: 'pointer',
    fontWeight: '600', fontSize: '11px', transition: 'all 0.15s'
  });

  const contentStyle = (isExpanded) => ({
    display: isExpanded ? 'block' : 'none', padding: '8px',
    backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderTop: 'none',
    borderRadius: '0 0 4px 4px'
  });

  const inputStyle = {
    width: '100%', padding: '4px 6px', fontSize: '11px',
    border: '1px solid var(--border)', borderRadius: '3px', outline: 'none'
  };

  const labelStyle = {
    fontSize: '11px', fontWeight: '500', marginBottom: '2px',
    display: 'block', color: 'var(--text-muted)'
  };

  const btnStyle = (color) => ({
    padding: '4px 10px', border: `1px solid ${color}`, color: color,
    fontSize: '11px', fontWeight: '500', marginRight: '4px'
  });

  // API calls
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/locations/');
      setLocations(data.data || data);
    } catch (err) {
      setError('Błąd pobierania lokalizacji');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data.data?.users || []);
    } catch (err) { console.error(err); }
  };

  const fetchLocationEmployees = async (locationId) => {
    if (!locationId) return;
    try {
      const { data } = await api.get(`/locations/${locationId}/employees`);
      setEmployees(data.data || []);
    } catch (err) { console.error(err); }
  };

  const addLocation = async () => {
    if (!newLocation.kod_lokalizacji.trim() || !newLocation.nazwa.trim()) {
      toast.error('Kod i nazwa są wymagane');
      return;
    }
    try {
      setLoading(true);
      await api.post('/locations/', newLocation);
      await fetchLocations();
      setNewLocation({ kod_lokalizacji: '', nazwa: '', typ: 'sklep', adres: '', miasto: '', kod_pocztowy: '', telefon: '', email: '', manager_login: '', aktywny: true, godziny_otwarcia: '' });
      toast.success('Dodano lokalizację');
    } catch (err) { toast.error('Błąd: ' + err.message); }
    finally { setLoading(false); }
  };

  const updateLocation = async () => {
    if (!editingLocation) return;
    try {
      setLoading(true);
      await api.put(`/locations/${editingLocation.id}`, editingLocation);
      await fetchLocations();
      setEditingLocation(null);
      toast.success('Zaktualizowano');
    } catch (err) { toast.error('Błąd'); }
    finally { setLoading(false); }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm('Usunąć lokalizację?')) return;
    try {
      await api.delete(`/locations/${id}`);
      await fetchLocations();
    } catch (err) { toast.error('Błąd'); }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocationId) { toast.error('Wybierz lokalizację'); return; }
    try {
      if (editingEmployee) {
        await api.put(`/locations/${selectedLocationId}/employees/${editingEmployee.id}`, employeeForm);
      } else {
        await api.post(`/locations/${selectedLocationId}/employees`, employeeForm);
      }
      await fetchLocationEmployees(selectedLocationId);
      setShowEmployeeModal(false);
      setEmployeeForm({ user_login: '', rola: 'pracownik', uprawnienia: '' });
      setEditingEmployee(null);
    } catch (err) { toast.error('Błąd'); }
  };

  const removeEmployee = async (id) => {
    if (!window.confirm('Usunąć przypisanie?')) return;
    try {
      await api.delete(`/locations/${selectedLocationId}/employees/${id}`);
      await fetchLocationEmployees(selectedLocationId);
    } catch (err) { toast.error('Błąd'); }
  };

  const addUser = async () => {
    if (!newUser.login || !newUser.haslo) { toast.error('Login i hasło wymagane'); return; }
    try {
      await api.post('/admin/users', newUser);
      await fetchUsers();
      setNewUser({ login: '', haslo: '', typ: 'kasjer' });
      toast.success('Dodano użytkownika');
    } catch (err) { toast.error('Błąd'); }
  };

  const updateUser = async () => {
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${editingUser.id}`, editingUser);
      await fetchUsers();
      setEditingUser(null);
    } catch (err) { toast.error('Błąd'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Usunąć użytkownika?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      await fetchUsers();
    } catch (err) { toast.error('Błąd'); }
  };

  useEffect(() => { fetchLocations(); fetchUsers(); }, []);
  useEffect(() => { if (selectedLocationId) fetchLocationEmployees(selectedLocationId); }, [selectedLocationId]);

  if (error) return <div style={{ padding: '8px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', fontSize: '11px' }}>❌ {error}</div>;

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '8px' }}>🏢 Zarządzanie Lokalizacjami</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Dodaj lokalizację */}
        <div>
          <div style={headerStyle(expandedSections.addLocation)} onClick={() => toggleSection('addLocation')}>
            <span>➕ Dodaj lokalizację</span>
            <span>{expandedSections.addLocation ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.addLocation)}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              <div>
                <label style={labelStyle}>Kod *</label>
                <input type="text" value={newLocation.kod_lokalizacji} onChange={(e) => setNewLocation({...newLocation, kod_lokalizacji: e.target.value})} placeholder="LOK001" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Nazwa *</label>
                <input type="text" value={newLocation.nazwa} onChange={(e) => setNewLocation({...newLocation, nazwa: e.target.value})} placeholder="Sklep Główny" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Typ</label>
                <select value={newLocation.typ} onChange={(e) => setNewLocation({...newLocation, typ: e.target.value})} style={inputStyle}>
                  <option value="sklep">Sklep</option>
                  <option value="magazyn">Magazyn</option>
                  <option value="hurtownia">Hurtownia</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Miasto</label>
                <input type="text" value={newLocation.miasto} onChange={(e) => setNewLocation({...newLocation, miasto: e.target.value})} placeholder="Warszawa" style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Adres</label>
                <input type="text" value={newLocation.adres} onChange={(e) => setNewLocation({...newLocation, adres: e.target.value})} placeholder="ul. Główna 1" style={inputStyle} />
              </div>
            </div>
            <button onClick={addLocation} disabled={loading} style={{ ...btnStyle('var(--success)'), marginTop: '8px' }}>
              {loading ? 'Dodawanie...' : '➕ Dodaj'}
            </button>
          </div>
        </div>

        {/* Lista lokalizacji */}
        <div>
          <div style={headerStyle(expandedSections.locations)} onClick={() => toggleSection('locations')}>
            <span>📍 Lokalizacje ({locations.length})</span>
            <span>{expandedSections.locations ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.locations)}>
            {loading ? <div style={{ textAlign: 'center', fontSize: '10px', padding: '8px' }}>Ładowanie...</div> : (
              <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--bg)' }}>
                    <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>ID</th>
                    <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Kod</th>
                    <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Nazwa</th>
                    <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Typ</th>
                    <th className="col-hide-mobile" style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Miasto</th>
                    <th style={{ padding: '4px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map(loc => (
                    <tr key={loc.id}>
                      <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{loc.id}</td>
                      <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}><code style={{ backgroundColor: 'var(--bg)', padding: '1px 3px', borderRadius: '2px' }}>{loc.kod_lokalizacji}</code></td>
                      <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)', fontWeight: '500' }}>{loc.nazwa}</td>
                      <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>
                        <span style={{ padding: '1px 4px', borderRadius: '2px', fontSize: '9px', backgroundColor: loc.typ === 'sklep' ? 'var(--success-light)' : 'var(--info-light)', color: loc.typ === 'sklep' ? 'var(--success)' : 'var(--info)' }}>{loc.typ}</span>
                      </td>
                      <td className="col-hide-mobile" style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{loc.miasto || '-'}</td>
                      <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)', textAlign: 'center' }}>
                        <button onClick={() => setEditingLocation({...loc})} style={btnStyle('var(--info)')}>✏️</button>
                        <button onClick={() => deleteLocation(loc.id)} style={btnStyle('var(--danger)')}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* Pracownicy */}
        <div>
          <div style={headerStyle(expandedSections.employees)} onClick={() => toggleSection('employees')}>
            <span>👥 Pracownicy lokalizacji</span>
            <span>{expandedSections.employees ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.employees)}>
            <div style={{ marginBottom: '8px' }}>
              <label style={labelStyle}>Wybierz lokalizację:</label>
              <select value={selectedLocationId} onChange={(e) => setSelectedLocationId(e.target.value)} style={{ ...inputStyle, maxWidth: '250px' }}>
                <option value="">-- Wybierz --</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.nazwa}</option>)}
              </select>
            </div>
            {selectedLocationId && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '500' }}>Pracownicy: {employees.length}</span>
                  <button onClick={() => { setEditingEmployee(null); setEmployeeForm({ user_login: '', rola: 'pracownik', uprawnienia: '' }); setShowEmployeeModal(true); }} style={btnStyle('var(--success)')}>➕ Przypisz</button>
                </div>
                <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg)' }}>
                      <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Login</th>
                      <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Rola</th>
                      <th style={{ padding: '4px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id}>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{emp.user_login}</td>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{emp.rola}</td>
                        <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)', textAlign: 'center' }}>
                          <button onClick={() => { setEditingEmployee(emp); setEmployeeForm({ user_login: emp.user_login, rola: emp.rola, uprawnienia: emp.uprawnienia || '' }); setShowEmployeeModal(true); }} style={btnStyle('var(--info)')}>✏️</button>
                          <button onClick={() => removeEmployee(emp.id)} style={btnStyle('var(--danger)')}>🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Użytkownicy */}
        <div>
          <div style={headerStyle(expandedSections.users)} onClick={() => toggleSection('users')}>
            <span>👨‍💼 Użytkownicy systemu ({users.length})</span>
            <span>{expandedSections.users ? '▼' : '▶'}</span>
          </div>
          <div style={contentStyle(expandedSections.users)}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '6px', marginBottom: '8px', alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Login</label>
                <input type="text" value={newUser.login} onChange={(e) => setNewUser({...newUser, login: e.target.value})} placeholder="login" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Hasło</label>
                <input type="password" value={newUser.haslo} onChange={(e) => setNewUser({...newUser, haslo: e.target.value})} placeholder="hasło" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Typ</label>
                <select value={newUser.typ} onChange={(e) => setNewUser({...newUser, typ: e.target.value})} style={inputStyle}>
                  <option value="kasjer">Kasjer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button onClick={addUser} style={btnStyle('var(--success)')}>➕</button>
            </div>
            <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>ID</th>
                  <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Login</th>
                  <th style={{ padding: '4px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Typ</th>
                  <th style={{ padding: '4px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{user.id}</td>
                    <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)', fontWeight: '500' }}>{user.login}</td>
                    <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)' }}>{user.role || user.user_type || user.typ}</td>
                    <td style={{ padding: '4px', borderBottom: '1px solid var(--bg)', textAlign: 'center' }}>
                      <button onClick={() => setEditingUser(user)} style={btnStyle('var(--info)')}>✏️</button>
                      <button onClick={() => deleteUser(user.id)} style={btnStyle('var(--danger)')}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal edycji lokalizacji */}
      {editingLocation && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '12px', borderRadius: '6px', width: 'min(95vw, 400px)', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '10px' }}>✏️ Edytuj lokalizację</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div><label style={labelStyle}>Kod</label><input type="text" value={editingLocation.kod_lokalizacji} onChange={(e) => setEditingLocation({...editingLocation, kod_lokalizacji: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Nazwa</label><input type="text" value={editingLocation.nazwa} onChange={(e) => setEditingLocation({...editingLocation, nazwa: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Typ</label>
                <select value={editingLocation.typ} onChange={(e) => setEditingLocation({...editingLocation, typ: e.target.value})} style={inputStyle}>
                  <option value="sklep">Sklep</option><option value="magazyn">Magazyn</option><option value="hurtownia">Hurtownia</option>
                </select>
              </div>
              <div><label style={labelStyle}>Miasto</label><input type="text" value={editingLocation.miasto || ''} onChange={(e) => setEditingLocation({...editingLocation, miasto: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Adres</label><input type="text" value={editingLocation.adres || ''} onChange={(e) => setEditingLocation({...editingLocation, adres: e.target.value})} style={inputStyle} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={editingLocation.aktywny} onChange={(e) => setEditingLocation({...editingLocation, aktywny: e.target.checked})} />
                <label style={{ fontSize: '11px' }}>Aktywna</label>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '10px' }}>
              <button onClick={() => setEditingLocation(null)} style={btnStyle('var(--text-muted)')}>Anuluj</button>
              <button onClick={updateLocation} style={btnStyle('var(--success)')}>💾 Zapisz</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pracownika */}
      {showEmployeeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '12px', borderRadius: '6px', width: 'min(95vw, 300px)' }}>
            <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '10px' }}>{editingEmployee ? '✏️ Edytuj' : '➕ Przypisz'} pracownika</div>
            <form onSubmit={handleEmployeeSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div><label style={labelStyle}>Użytkownik</label>
                  <select value={employeeForm.user_login} onChange={(e) => setEmployeeForm({...employeeForm, user_login: e.target.value})} style={inputStyle} required>
                    <option value="">-- Wybierz --</option>
                    {users.map(u => <option key={u.id} value={u.login}>{u.login}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Rola</label>
                  <select value={employeeForm.rola} onChange={(e) => setEmployeeForm({...employeeForm, rola: e.target.value})} style={inputStyle}>
                    <option value="pracownik">Pracownik</option>
                    <option value="kasjer">Kasjer</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '10px' }}>
                <button type="button" onClick={() => setShowEmployeeModal(false)} style={btnStyle('var(--text-muted)')}>Anuluj</button>
                <button type="submit" style={btnStyle('var(--success)')}>💾 Zapisz</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal edycji użytkownika */}
      {editingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '12px', borderRadius: '6px', width: 'min(95vw, 300px)' }}>
            <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '10px' }}>✏️ Edytuj użytkownika</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div><label style={labelStyle}>Login</label><input type="text" value={editingUser.login} onChange={(e) => setEditingUser({...editingUser, login: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Nowe hasło (opcjonalne)</label><input type="password" value={editingUser.haslo || ''} onChange={(e) => setEditingUser({...editingUser, haslo: e.target.value})} placeholder="pozostaw puste" style={inputStyle} /></div>
              <div><label style={labelStyle}>Typ</label>
                <select value={editingUser.typ || editingUser.user_type || editingUser.role || 'kasjer'} onChange={(e) => setEditingUser({...editingUser, typ: e.target.value})} style={inputStyle}>
                  <option value="kasjer">Kasjer</option><option value="manager">Manager</option><option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '10px' }}>
              <button onClick={() => setEditingUser(null)} style={btnStyle('var(--text-muted)')}>Anuluj</button>
              <button onClick={updateUser} style={btnStyle('var(--success)')}>💾 Zapisz</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsTab;
